import {
  HStack,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FaListUl, FaRegComment } from 'react-icons/fa'
import { FiFileMinus } from 'react-icons/fi'
import { useLocation } from 'react-router-dom'
import type { TabKey } from '../../../providers/UserPreferencesProvider'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import { useUserPreferences } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import CustomMetadata from './CustomMetadata'
import CustomMetadataForm from './CustomMetadataForm'
import FileSummary from './FileSummary'
import ThreadList from './Threads/ThreadList'

const FILE_TABS: ReadonlyArray<{
  key: TabKey
  labelId: string
  icon: React.ComponentType<{ size?: string | number }>
}> = [
  { key: 'summary', labelId: 'summary', icon: FaListUl },
  {
    key: 'customMetadata',
    labelId: 'Custom metadata',
    icon: FiFileMinus,
  },
  { key: 'comments', labelId: 'comments', icon: FaRegComment },
]

function TabLabel({
  icon,
  children,
  isSelected,
}: {
  icon: React.ComponentType<{ size?: string | number }>
  children: React.ReactNode
  isSelected?: boolean
}): ReactElement {
  return (
    <HStack spacing={2} maxW="100%" gap="4px">
      <Icon
        as={icon}
        boxSize={4}
        color={isSelected ? 'appBlue' : 'appBlueyGrey'}
      />
      <Text
        whiteSpace="normal"
        color={isSelected ? 'appBlue' : 'imagekitGray.700'}
      >
        {children}
      </Text>
    </HStack>
  )
}

const MLRightSidebar = ({
  fileDetails,
  isDetailPage,
}: {
  fileDetails: FileDetailsResponse
  isDetailPage: boolean
}): ReactElement => {
  const { t } = useTranslation()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { prefs, setLastTab } = useUserPreferences()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const focusedThreadIdParam = searchParams.get('focusedThreadId')

  useEffect(() => {
    if (!isPublicLink && focusedThreadIdParam) {
      setLastTab('comments')
    }
  }, [focusedThreadIdParam, isPublicLink, setLastTab])

  const allTabs = FILE_TABS.map(ti => ({
    key: ti.key,
    label: t(ti.labelId, ti.labelId),
    icon: ti.icon,
  }))

  const visibleTabs = isPublicLink
    ? allTabs.filter(tab => tab.key === 'summary')
    : isDetailPage
      ? allTabs
      : allTabs.filter(tab => tab.key !== 'comments')

  // Determine preferred tab - force summary for public links, or use lastTab with fallback
  const preferredKey: TabKey = isPublicLink
    ? 'summary'
    : prefs.lastTab

  const index = (() => {
    const i = visibleTabs.findIndex(tab => tab.key === preferredKey)
    if (i >= 0) {
      return i
    }
    // Fallback to summary if preferred tab is not available
    return Math.max(
      0,
      visibleTabs.findIndex(tab => tab.key === 'summary'),
    )
  })()

  return (
    <Tabs
      index={index}
      onChange={i => {
        const key = visibleTabs[i]?.key ?? 'summary'
        setLastTab(key)
      }}
      display="flex"
      flexDirection="column"
      minH="0"
      height="100%"
      size="md"
      isLazy
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.stopPropagation()
        }
      }}
    >
      <TabList>
        {visibleTabs.map((tab, i) => (
          <Tab
            key={tab.key}
            flexGrow={1}
            borderBottom="2px solid transparent"
            _selected={{
              borderColor: 'appBlue',
              bg: '#f0f5fd',
              fontWeight: 600,
            }}
            _focus={{ outline: 'none' }}
          >
            <TabLabel icon={tab.icon} isSelected={index === i}>
              {tab.label}
            </TabLabel>
          </Tab>
        ))}
      </TabList>

      <TabPanels display="flex" flex="1 1 0" minH={0} overflow="auto">
        {visibleTabs.map(tab => {
          switch (tab.key) {
            case 'summary':
              return (
                <TabPanel key="summary" display="flex" p="0" width="100%">
                  <FileSummary fileDetails={fileDetails} />
                </TabPanel>
              )
            case 'customMetadata':
              return (
                <TabPanel key="customMetadata" display="flex" flexGrow={1}>
                  {fileDetails.type === 'file' ? (
                    <CustomMetadataForm fileDetails={fileDetails} />
                  ) : (
                    <CustomMetadata fileDetails={fileDetails} />
                  )}
                </TabPanel>
              )
            case 'comments':
              return (
                <TabPanel
                  key="comments"
                  display="flex"
                  flexGrow={1}
                  p="0"
                  overflowX="hidden"
                >
                  <ThreadList fileDetails={fileDetails} />
                </TabPanel>
              )
            default:
              return null
          }
        })}
      </TabPanels>
    </Tabs>
  )
}

export default MLRightSidebar
